import { useCallback, useEffect, useMemo, useState } from "react";
import { MainSurveyStore } from "modules/MainSurveyEditor/hooks/useMainSurvey";
import { useMainSurveyData } from "modules/MainSurveyEditor/hooks/useMainSurveyData";
import { Conditions as ConditionTypes } from "../conditions";
import { ConditionActionTypes } from "../helpers/conditions";
import { conditionClient } from "../client/conditionClient";
import { SurveyBlocks } from "modules/MainSurveyEditor/surveyBlocks";
import { getParentPath } from "modules/MainSurveyEditor/helpers/blockTree";
import { get } from "lodash";

export const useConditions = (mainSurveyStore: MainSurveyStore, isSubBlock: boolean) => {
  const { blockToEdit: block, mainSurveyTree, setIsSaving, handleSurveySaved, subBlockToEdit: subBlock } = mainSurveyStore;

  const blockToEdit = (isSubBlock && subBlock) ? subBlock : block;

  const { surveyBlock, isLoading: isSurveyBlockLoading } = useMainSurveyData(
    blockToEdit?.parentSurveyId,
    blockToEdit?.id
  );
  const [conditions, setConditions] = useState<Partial<ConditionTypes.Condition>[]>([]);
  const [selectPath, setSelectPath] = useState<string>("");

  const selectGoBackAction = useMemo(() => {
    if (!selectPath) {
      return undefined;
    }

    const goBackPath = getParentPath(selectPath);
    return {
      title: get(mainSurveyTree, selectPath)?.title,
      onClick: () => setSelectPath(goBackPath),
    };
  }, [mainSurveyTree, selectPath]);

  const addCondition = useCallback((actionType: ConditionActionTypes) => {
    setConditions((old) => [...old, { actionType }]);
  }, []);

  const deleteCondition = useCallback(
    (index: number) => {
      if (conditions[index]?.id) {
        conditionClient.deleteCondition(blockToEdit.parentSurveyId, blockToEdit.id!, conditions[index].id!);
      }

      setConditions((old) => {
        const temp = [...old];
        temp.splice(index, 1);
        return temp;
      });
    },
    [blockToEdit, conditions]
  );

  const onUpdate = useCallback(
    async (index: number, conditionUpdate: Partial<ConditionTypes.Condition>) => {
      const temp = [...conditions];
      const conditionApi = { ...conditionUpdate };
      delete conditionApi.contentObject;

      temp.splice(index, 1, { ...temp[index], ...conditionUpdate });
      setConditions(temp);

      const isNewCondition = !temp[index].id;
      setIsSaving(true);
      if (isNewCondition) {
        const condition = await conditionClient.createCondition(
          blockToEdit.parentSurveyId,
          blockToEdit.id!,
          conditionApi
        );
        temp.splice(index, 1, { ...temp[index], id: condition.data.id });
        setConditions(temp);
      } else {
        await conditionClient.updateCondition(
          blockToEdit.parentSurveyId,
          blockToEdit.id!,
          temp[index].id!,
          conditionApi
        );
      }
      handleSurveySaved();
    },
    [blockToEdit, conditions, handleSurveySaved, setIsSaving]
  );

  useEffect(() => {
    if (!surveyBlock || isSurveyBlockLoading) {
      return;
    }

    setConditions((surveyBlock as SurveyBlocks.Block).conditions);
  }, [isSurveyBlockLoading, surveyBlock]);

  useEffect(() => setSelectPath(""), [mainSurveyTree]);

  return {
    selectPath,
    setSelectPath,
    selectGoBackAction,
    surveyBlock,
    isSurveyBlockLoading,
    conditions,
    setConditions,
    addCondition,
    deleteCondition,
    onUpdate,
  };
};
